import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import Seo from "../components/Seo";
import { PublicJobCard } from "./Landing";

const COUNTRY_NAMES = {
  at: "Austria", be: "Belgium", bg: "Bulgaria", hr: "Croatia", cy: "Cyprus", cz: "Czechia",
  dk: "Denmark", ee: "Estonia", fi: "Finland", fr: "France", de: "Germany", gr: "Greece",
  hu: "Hungary", ie: "Ireland", it: "Italy", lv: "Latvia", lt: "Lithuania", lu: "Luxembourg",
  mt: "Malta", nl: "Netherlands", no: "Norway", pl: "Poland", pt: "Portugal", ro: "Romania",
  sk: "Slovakia", si: "Slovenia", es: "Spain", se: "Sweden", ch: "Switzerland",
  gb: "United Kingdom", ua: "Ukraine",
};

export function CourierJobsIndex() {
  const [coverage, setCoverage] = useState([]);
  useEffect(() => { api.get("/public/coverage").then((r) => setCoverage(r.data.countries)).catch(() => {}); }, []);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="courier-jobs-index">
      <Seo title="Courier jobs in Europe | CourierRadar"
        description="Browse courier and transport jobs by country across the UK, EU/EEA and wider Europe."
        canonical="https://courierradar.com/courier-jobs"
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: "Courier jobs in Europe" }} />
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Courier jobs in Europe</h1>
      <p className="text-sm text-zinc-500 mb-8">Choose a country to see open jobs.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {coverage.map((c) => (
          <Link key={c.code} to={`/courier-jobs/${c.code}`} data-testid={`country-${c.code}`}
            className="border border-zinc-200 rounded-lg px-4 py-3 text-sm hover:border-black/30 transition-colors flex items-center justify-between">
            <span>{c.name}</span>
            {c.jobs > 0 && <span className="text-[11px] font-bold bg-black text-white rounded-full px-2 py-0.5">{c.jobs}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function CourierJobsCountry() {
  const { country, city } = useParams();
  const name = COUNTRY_NAMES[(country || "").toLowerCase()] || country?.toUpperCase();
  const cityName = city ? city.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null;
  const [jobs, setJobs] = useState(null);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    setJobs(null);
    api.get("/public/jobs/search", { params: { country, city: cityName || undefined } })
      .then((r) => setJobs(r.data.jobs)).catch(() => setJobs([]));
    api.get("/public/coverage").then((r) => {
      const c = r.data.countries.find((x) => x.code === country);
      setCities(c?.cities || []);
    }).catch(() => {});
  }, [country, city, cityName]);

  const title = cityName ? `Courier jobs in ${cityName}, ${name}` : `Courier jobs in ${name}`;
  const crumbs = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", position: 1, name: "Courier jobs in Europe", item: "https://courierradar.com/courier-jobs" },
      { "@type": "ListItem", position: 2, name, item: `https://courierradar.com/courier-jobs/${country}` },
      ...(cityName ? [{ "@type": "ListItem", position: 3, name: cityName }] : []),
    ],
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="courier-jobs-country">
      <Seo title={`${title} | CourierRadar`}
        description={`Open courier and transport jobs in ${cityName ? `${cityName}, ` : ""}${name}. Bid and deliver with CourierRadar.`}
        canonical={`https://courierradar.com/courier-jobs/${country}${city ? `/${city}` : ""}`}
        jsonLd={crumbs} />
      <nav className="text-xs text-zinc-400 mb-4" aria-label="Breadcrumb">
        <Link to="/courier-jobs" className="hover:text-black">Europe</Link>
        {cityName && <> <span className="mx-1">/</span> <Link to={`/courier-jobs/${country}`} className="hover:text-black">{name}</Link></>}
      </nav>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">{title}</h1>
      <p className="text-sm text-zinc-500 mb-6">
        {jobs === null ? "Loading…" : jobs.length > 0 ? `${jobs.length} open job${jobs.length !== 1 ? "s" : ""} right now.` : "No open jobs here right now. New jobs are posted daily."}
      </p>
      {!cityName && cities.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {cities.map((c) => (
            <Link key={c} to={`/courier-jobs/${country}/${c.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-xs border border-zinc-200 rounded-full px-3 py-1.5 hover:border-black/30 transition-colors">{c}</Link>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(jobs || []).map((j) => <PublicJobCard key={j.ref} job={j} />)}
      </div>
      <p className="text-xs text-zinc-400 mt-8">
        <Link to="/auth?mode=register&role=courier" className="text-black font-semibold underline underline-offset-2">Sign up as a courier</Link> to bid on jobs in {name}.
      </p>
    </div>
  );
}
